import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import {
  isAgency,
  isAllocator,
  isYucollectAdmin,
} from 'features/Yucollect/utils/util';
import { put, select, takeLatest } from 'redux-saga/effects';
import { RootReducerState } from 'redux/slice/reducer';
import {
  clearDownloadReport,
  downloadCallReport,
  downloadCallReportSuccess,
  downloadDigitalReport,
  downloadDigitalReportSuccess,
  downloadReport,
  downloadReportFailure,
  downloadReportSuccess,
  downloadWhatsAppReport,
  downloadWhatsAppReportFailure,
  downloadWhatsAppReportSuccess,
  fetchDataGrid,
  fetchDataGridFailure,
  fetchDataGridSuccess,
  fetchFilters,
  fetchFiltersFailure,
  fetchFiltersSuccess,
  getCallReports,
  getCallReportsFailure,
  getCallReportsSuccess,
  getDigitalReports,
  getDigitalReportsFailure,
  getDigitalReportsSuccess,
  getWhatsAppReports,
  getWhatsAppReportsFailure,
  getWhatsAppReportsSuccess,
  requestCallReport,
  requestCallReportFailure,
  requestCallReportSuccess,
  requestDigitalReport,
  requestDigitalReportFailure,
  requestDigitalReportSuccess,
  requestWhatsAppReport,
  requestWhatsAppReportFailure,
  requestWhatsAppReportSuccess,
} from 'redux/slice/report/Reports.slice';
import {
  DataGridRequest,
  DataGridResponse,
  DownloadCallReportResponse,
  DownloadDigitalReportResponse,
  DownloadReportRequest,
  DownloadReportResponse,
  FilterRequest,
  FilterResponse,
  GetCallReportRequest,
  GetCallReportResponse,
  GetDigitalReportRequest,
  GetDigitalReportResponse,
  GetWhatsAppReportRequest,
  GetWhatsAppReportResponse,
  RequestCallReportRequest,
  RequestCallReportResponse,
  RequestDigitalReportRequest,
  RequestDigitalReportResponse,
  RequestWhatsAppReportRequest,
  RequestWhatsAppReportResponse,
} from 'redux/types/Reports.types';
import { reportsEndPoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

import { handleError } from '../utils/errorUtils';

// Saga for fetching filters
function* fetchFiltersSaga(action: PayloadAction<FilterRequest>) {
  try {
    const response: AxiosResponse<FilterResponse> =
      yield HTTPClient.PostRequest(
        reportsEndPoints.APPLY_FILTERS,
        action.payload
      );
    yield put(fetchFiltersSuccess(response.data));
    message.success('Filters fetched successfully!');
  } catch (error: unknown) {
    yield* handleError(error, fetchFiltersFailure, '');
  }
}

// Saga for fetching data grid
function* fetchDataGridSaga(action: PayloadAction<DataGridRequest>) {
  const { agencyId, allocatorId, channel, pageSize, page, searchQuery } =
    action.payload;

  try {
    let url = reportsEndPoints.GET_REPORTS_BASE_URL(allocatorId);

    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('pageSize', pageSize.toString());

    if (searchQuery) {
      queryParams.append('search', searchQuery);
    }

    if (agencyId) {
      queryParams.append('agencyId', agencyId);
    }

    if (channel && channel.length > 0) {
      queryParams.append('channelType', channel[0]);
    }

    // Append query parameters to the URL
    url = `${url}?${queryParams.toString()}`;

    const response: AxiosResponse<DataGridResponse> =
      yield HTTPClient.GetRequest(url);

    yield put(fetchDataGridSuccess(response.data));
  } catch (error) {
    yield* handleError(error, fetchDataGridFailure, '');
  }
}

// Saga for downloading report
function* downloadReportSaga(action: PayloadAction<DownloadReportRequest>) {
  const {
    reportType,
    channel,
    agencyId,
    allocatorId,
    allocationFileId,
    fromDate,
    toDate,
    exportType,
  } = action.payload;
  try {
    const payload =
      reportType === 'apr-report'
        ? {
            agencyId,
            allocatorId,
            fromDate,
            toDate,
            exportType,
          }
        : {
            agencyId,
            allocatorId,
            allocationFileId,
            fromDate,
            toDate,
            exportType,
          };

    const response: AxiosResponse<DownloadReportResponse> =
      yield HTTPClient.PostRequest(
        reportsEndPoints.DOWNLOAD_REPORT(reportType, channel),
        payload
      );
    yield put(downloadReportSuccess(response.data));
    if (!response.data.reportFileUrl) {
      message.error('No report is available');
    } else {
      message.success(response.data.message);
      window.open(response.data.reportFileUrl, '_blank');
      yield put(clearDownloadReport());
    }
  } catch (error: unknown) {
    yield* handleError(error, downloadReportFailure, '');
  }
}

// Saga for downloading call report
function* downloadCallReportSaga(action: PayloadAction<{ id: string }>) {
  const { id } = action.payload;
  try {
    const response: AxiosResponse<DownloadCallReportResponse> =
      yield HTTPClient.GetRequest<DownloadCallReportResponse>(
        reportsEndPoints.DOWNLOAD_CALL_REPORT(id)
      );
    yield put(downloadCallReportSuccess(response.data));
    if (!response.data.reportFileUrl) {
      message.error('No report is available');
    } else {
      message.success(response.data.message);
      window.open(response.data.reportFileUrl, '_blank');
      yield put(clearDownloadReport());
    }
  } catch (error: unknown) {
    yield* handleError(error, downloadReportFailure, '');
  }
}

// Saga for requesting call report
function* requestCallReportSaga(
  action: PayloadAction<RequestCallReportRequest>
) {
  const { reportType, listReportType, ...restPayload } = action.payload;
  // Use listReportType for the list API if available (when reportType is 'allocator'), otherwise use reportType
  const reportTypeForList = listReportType || reportType;
  try {
    const response: AxiosResponse<RequestCallReportResponse> =
      yield HTTPClient.PostRequest<RequestCallReportResponse>(
        reportsEndPoints.REQUEST_CALL_REPORT(reportType),
        restPayload
      );
    yield put(requestCallReportSuccess(response.data));
    message.success(response.data.message);

    const userConfiguration = (state: RootReducerState) =>
      state.userConfiguration;
    const {
      userGroup,
      lenderId,
      agencyId: AGENCY_ID,
    } = yield select(userConfiguration) || {};

    const isYucollectOps = isYucollectAdmin(userGroup);
    const isAgencyUser = isAgency(userGroup);
    const isAllocatorUser = isAllocator(userGroup);

    // Call getCallReports with appropriate params based on user type
    if (isYucollectOps) {
      yield put(
        getCallReports({
          reportType: reportTypeForList,
          agencyId: '', // Always empty for YUCOLLECT_OPS
          allocatorId: '', // Always empty for YUCOLLECT_OPS
          page: 0,
          pageSize: 10,
        })
      );
    } else if (isAllocatorUser) {
      yield put(
        getCallReports({
          reportType: reportTypeForList,
          agencyId: '',
          allocatorId: lenderId || '',
          page: 0,
          pageSize: 10,
        })
      );
    } else if (isAgencyUser) {
      yield put(
        getCallReports({
          reportType: reportTypeForList,
          agencyId: AGENCY_ID || '',
          allocatorId: lenderId || '',
          page: 0,
          pageSize: 10,
        })
      );
    } else {
      yield put(
        getCallReports({
          reportType: reportTypeForList,
          agencyId: AGENCY_ID || '',
          allocatorId: lenderId || '',
          page: 0,
          pageSize: 10,
        })
      );
    }
  } catch (error: unknown) {
    yield* handleError(error, requestCallReportFailure, '');
  }
}

// Saga for getting call reports
function* getCallReportsSaga(action: PayloadAction<GetCallReportRequest>) {
  const { reportType, agencyId, allocatorId, page, pageSize } = action.payload;

  try {
    let url = reportsEndPoints.GET_CALL_REPORTS();

    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('pageSize', pageSize.toString());

    if (allocatorId) {
      queryParams.append('allocatorId', allocatorId);
    }

    if (agencyId) {
      queryParams.append('agencyId', agencyId);
    }

    if (reportType) {
      queryParams.append('reportType', reportType);
    }

    // Append query parameters to the URL
    url = `${url}?${queryParams.toString()}`;

    const response: AxiosResponse<GetCallReportResponse> =
      yield HTTPClient.GetRequest(url);

    yield put(getCallReportsSuccess(response.data));
  } catch (error) {
    yield* handleError(error, getCallReportsFailure, '');
  }
}

// Saga for downloading digital report
function* downloadDigitalReportSaga(action: PayloadAction<{ id: string }>) {
  const { id } = action.payload;
  try {
    const response: AxiosResponse<DownloadDigitalReportResponse> =
      yield HTTPClient.GetRequest<DownloadDigitalReportResponse>(
        reportsEndPoints.DOWNLOAD_DIGITAL_REPORT(id)
      );
    yield put(downloadDigitalReportSuccess(response.data));
    if (!response.data.reportFileUrl) {
      message.error('No report is available');
    } else {
      message.success(response.data.message);
      window.open(response.data.reportFileUrl, '_blank');
      yield put(clearDownloadReport());
    }
  } catch (error: unknown) {
    yield* handleError(error, downloadReportFailure, '');
  }
}

// Saga for requesting digital report
function* requestDigitalReportSaga(
  action: PayloadAction<RequestDigitalReportRequest>
) {
  const {
    reportType,
    allocatorId,
    allocatorName,
    agencyId,
    agencyName,
    requestedBy,
    fromDate,
    toDate,
  } = action.payload;
  try {
    const response: AxiosResponse<RequestDigitalReportResponse> =
      yield HTTPClient.PostRequest<RequestDigitalReportResponse>(
        reportsEndPoints.REQUEST_DIGITAL_REPORT(reportType),
        {
          reportType,
          allocatorId,
          allocatorName,
          agencyId,
          agencyName,
          requestedBy,
          fromDate,
          toDate,
        }
      );
    yield put(requestDigitalReportSuccess(response.data));
    message.success(response.data.message);

    const userConfiguration = (state: RootReducerState) =>
      state.userConfiguration;
    const {
      userGroup,
      lenderId,
      agencyId: AGENCY_ID,
    } = yield select(userConfiguration) || {};

    const isYucollectOps = isYucollectAdmin(userGroup);
    const isAgencyUser = isAgency(userGroup);
    const isAllocatorUser = isAllocator(userGroup);

    // Call getDigitalReports with appropriate params based on user type
    if (isYucollectOps) {
      yield put(
        getDigitalReports({
          agencyId: '',
          allocatorId: '',
          page: 0,
          pageSize: 10,
        })
      );
    } else if (isAllocatorUser) {
      yield put(
        getDigitalReports({
          agencyId: '',
          allocatorId: lenderId || '',
          page: 0,
          pageSize: 10,
        })
      );
    } else if (isAgencyUser) {
      yield put(
        getDigitalReports({
          agencyId: agencyId || '',
          allocatorId: lenderId || '',
          page: 0,
          pageSize: 10,
        })
      );
    } else {
      yield put(
        getDigitalReports({
          agencyId: AGENCY_ID || '',
          allocatorId: lenderId || '',
          page: 0,
          pageSize: 10,
        })
      );
    }
  } catch (error: unknown) {
    yield* handleError(error, requestDigitalReportFailure, '');
  }
}

// Saga for getting digital reports
function* getDigitalReportsSaga(
  action: PayloadAction<GetDigitalReportRequest>
) {
  const { agencyId, allocatorId, page, pageSize } = action.payload;

  try {
    let url = reportsEndPoints.GET_DIGITAL_REPORTS();

    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('pageSize', pageSize.toString());

    if (allocatorId) {
      queryParams.append('allocatorId', allocatorId);
    }

    if (agencyId) {
      queryParams.append('agencyId', agencyId);
    }

    // Append query parameters to the URL
    url = `${url}?${queryParams.toString()}`;

    const response: AxiosResponse<GetDigitalReportResponse> =
      yield HTTPClient.GetRequest(url);

    yield put(getDigitalReportsSuccess(response.data));
  } catch (error) {
    yield* handleError(error, getDigitalReportsFailure, '');
  }
}

// Saga for requesting WhatsApp report
function* requestWhatsAppReportSaga(
  action: PayloadAction<RequestWhatsAppReportRequest>
) {
  try {
    const response: AxiosResponse<RequestWhatsAppReportResponse> =
      yield HTTPClient.PostRequest<RequestWhatsAppReportResponse>(
        reportsEndPoints.REQUEST_WHATSAPP_REPORT(),
        action.payload
      );
    yield put(requestWhatsAppReportSuccess(response.data));
    message.success('Report generation initiated');
    yield put(getWhatsAppReports({ page: 0, size: 10 }));
  } catch (error: unknown) {
    yield* handleError(error, requestWhatsAppReportFailure, '');
  }
}

// Saga for getting WhatsApp reports list
function* getWhatsAppReportsSaga(
  action: PayloadAction<GetWhatsAppReportRequest>
) {
  const { page, size } = action.payload;
  try {
    const url = reportsEndPoints.GET_WHATSAPP_REPORTS(page, size);
    const response: AxiosResponse<GetWhatsAppReportResponse> =
      yield HTTPClient.GetRequest(url);
    yield put(getWhatsAppReportsSuccess(response.data));
  } catch (error) {
    yield* handleError(error, getWhatsAppReportsFailure, '');
  }
}

// Saga for downloading WhatsApp report
function* downloadWhatsAppReportSaga(action: PayloadAction<{ id: string }>) {
  const { id } = action.payload;
  try {
    const response: AxiosResponse<{ downloadUrl: string; expiresAt: string }> =
      yield HTTPClient.GetRequest(
        reportsEndPoints.DOWNLOAD_WHATSAPP_REPORT(id)
      );
    yield put(downloadWhatsAppReportSuccess());
    if (!response.data.downloadUrl) {
      message.error('No report is available');
    } else {
      window.open(response.data.downloadUrl, '_blank');
    }
  } catch (error: unknown) {
    yield* handleError(error, downloadWhatsAppReportFailure, '');
  }
}

export function* watchReportsSagas() {
  yield takeLatest(fetchFilters.type, fetchFiltersSaga);
  yield takeLatest(fetchDataGrid.type, fetchDataGridSaga);
  yield takeLatest(downloadReport.type, downloadReportSaga);
  yield takeLatest(downloadCallReport.type, downloadCallReportSaga);
  yield takeLatest(requestCallReport.type, requestCallReportSaga);
  yield takeLatest(getCallReports.type, getCallReportsSaga);
  yield takeLatest(downloadDigitalReport.type, downloadDigitalReportSaga);
  yield takeLatest(requestDigitalReport.type, requestDigitalReportSaga);
  yield takeLatest(getDigitalReports.type, getDigitalReportsSaga);
  yield takeLatest(requestWhatsAppReport.type, requestWhatsAppReportSaga);
  yield takeLatest(getWhatsAppReports.type, getWhatsAppReportsSaga);
  yield takeLatest(downloadWhatsAppReport.type, downloadWhatsAppReportSaga);
}
